import type { Canvas } from 'fabric';
import type { SetStateAction } from 'react';
import { type MutableRefObject, useCallback } from 'react';
import type { SlideData, VariantKey } from '../store/atoms/slideAtom';

export interface UseBackgroundControlsParams {
  readonly getActiveCanvas: () => Canvas | null;
  readonly variantKey: VariantKey;
  readonly saveCurrentSlide: (key: VariantKey) => void;
  readonly setSlide: (update: SetStateAction<SlideData>) => void;
  readonly slideRef: MutableRefObject<SlideData>;
  readonly setIsDirty: (dirty: boolean) => void;
  /** Setter de cor de fundo bruto (sem marcar dirty), vindo de `useObjectTransform`. */
  readonly setBackgroundRaw: (color: string) => void;
  /** Setter de imagem de fundo bruto (sem marcar dirty), vindo de `useObjectTransform`. */
  readonly setBackgroundImageRaw: (dataUrl: string) => void;
  /** Remoção de imagem de fundo bruta (sem marcar dirty), vinda de `useObjectTransform`. */
  readonly removeBackgroundImageRaw: () => void;
}

/**
 * Controles de fundo da variante ativa (cor, imagem e vídeo).
 *
 * Envolve os setters brutos de `useObjectTransform` para marcar o estado como
 * `dirty` e adiciona a lógica de vídeo de fundo (exclusivo com imagem).
 *
 * @param params - Dependências de canvas, slide e setters brutos
 * @returns Setters de fundo (`setBackground`, imagem e vídeo)
 */
export function useBackgroundControls({
  getActiveCanvas,
  variantKey,
  saveCurrentSlide,
  setSlide,
  slideRef,
  setIsDirty,
  setBackgroundRaw,
  setBackgroundImageRaw,
  removeBackgroundImageRaw,
}: UseBackgroundControlsParams) {
  const setBackground = useCallback(
    (color: string) => {
      setIsDirty(true);
      setBackgroundRaw(color);
    },
    [setBackgroundRaw, setIsDirty],
  );

  const setBackgroundImage = useCallback(
    (dataUrl: string) => {
      setIsDirty(true);
      setBackgroundImageRaw(dataUrl);
    },
    [setBackgroundImageRaw, setIsDirty],
  );

  const removeBackgroundImage = useCallback(() => {
    setIsDirty(true);
    removeBackgroundImageRaw();
  }, [removeBackgroundImageRaw, setIsDirty]);

  const setBackgroundVideo = useCallback(
    (url: string) => {
      const canvas = getActiveCanvas();
      if (!canvas) return;
      setIsDirty(true);
      // Cor de fundo atual preservada como fallback (o canvas fica transparente).
      const prevColor = slideRef.current.variants[variantKey]?.backgroundColor ?? '#141414';
      // Vídeo e imagem são exclusivos — remove a imagem do canvas.
      Object.assign(canvas, { backgroundImage: undefined });
      canvas.backgroundColor = 'transparent';
      canvas.requestRenderAll();
      saveCurrentSlide(variantKey);
      setSlide(prev => {
        const { backgroundImage: _img, ...rest } = prev.variants[variantKey];
        return {
          ...prev,
          variants: {
            ...prev.variants,
            [variantKey]: { ...rest, backgroundColor: prevColor, backgroundVideo: url },
          },
        };
      });
    },
    [getActiveCanvas, variantKey, saveCurrentSlide, setSlide, slideRef, setIsDirty],
  );

  const removeBackgroundVideo = useCallback(() => {
    const canvas = getActiveCanvas();
    if (!canvas) return;
    setIsDirty(true);
    const prevColor = slideRef.current.variants[variantKey]?.backgroundColor ?? '#141414';
    canvas.backgroundColor = prevColor;
    canvas.requestRenderAll();
    saveCurrentSlide(variantKey);
    setSlide(prev => {
      const { backgroundVideo: _video, ...rest } = prev.variants[variantKey];
      return {
        ...prev,
        variants: { ...prev.variants, [variantKey]: { ...rest, backgroundColor: prevColor } },
      };
    });
  }, [getActiveCanvas, variantKey, saveCurrentSlide, setSlide, slideRef, setIsDirty]);

  return {
    setBackground,
    setBackgroundImage,
    removeBackgroundImage,
    setBackgroundVideo,
    removeBackgroundVideo,
  } as const;
}
